import { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  fetchStaff,
  createStaffPerson,
  updateStaffPerson,
  deleteStaffPerson,
  selectStaffPersons,
} from '../../../reducers/maintenanceStaffReducer'
import { ABILITIES } from '../../../components/Can'
import { selectUserData } from '../../../reducers/userDataReducer'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

import MaintenanceStaffForm from './MaintenanceStaffForm'
import MaintenanceStaffList from './MaintenanceStaffList'

const MaintenanceStaff = ({
  fetchStaff,
  createStaffPerson,
  updateStaffPerson,
  deleteStaffPerson,
  staffPersons,
  userData,
}) => {
  const { t } = useTranslation(['maintenance'])
  const [isLoading, setLoading] = useState(false)
  const [isDeletePending, setDeletePending] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { displaySnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { staffId, buildingId } = useParams()

  const currentStuffMember = useMemo(() => {
    if (staffPersons.length && staffId) {
      return staffPersons.find((staffMember) => staffMember.id === Number(staffId)) || null
    } else return null
  }, [staffPersons, staffId])

  const isFormOpen = useMemo(() => {
    return staffId !== undefined
  }, [staffId])

  useEffect(() => {
    ;(async () => {
      if (buildingId) {
        setLoading(true)
        await fetchStaff(buildingId)
        setLoading(false)
      }
    })()
  }, [buildingId])

  useEffect(() => {
    if (!ABILITIES.Navbar.maintenance_staff.includes(userData.user.role)) {
      navigate('/')
    }
  }, [])

  const createOrUpdateStuff = async (values, handlers) => {
    try {
      const staffData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        notifications: values.notifications,
        category: values.category,
      }

      if (values.avatar.file) {
        staffData.avatar = values.avatar.image.file
      }

      if (currentStuffMember && buildingId) {
        await updateStaffPerson(buildingId, currentStuffMember.id, staffData)
        displaySnackbar(t('maintenance:staff.snackbar.updated'))
      }

      if (!currentStuffMember && buildingId) {
        const newMember = await createStaffPerson(buildingId, staffData)
        addStaffIdRouteParam(newMember.id)
        displaySnackbar(t('maintenance:staff.snackbar.created'))
      }
    } catch ({ message }) {
      setErrorMessage(message)
    } finally {
      handlers.setSubmitting(false)
    }
  }

  const onSelectStaffMember = (member) => {
    addStaffIdRouteParam(member.id)
  }

  const onAddNewClick = () => {
    setErrorMessage('')
    addStaffIdRouteParam(0)
  }

  const addStaffIdRouteParam = (staffId) => {
    const path = `/dashboard/building-dashboard/maintenance/staff`
    const requestItemPath =
      staffId !== undefined ? `${path}/${buildingId}/${staffId}` : `${path}/${buildingId}`
    navigate(requestItemPath, { replace: true })
  }

  const onDeleteStaff = async () => {
    try {
      setDeletePending(true)
      setErrorMessage('')
      if (currentStuffMember && buildingId) {
        await deleteStaffPerson(buildingId, currentStuffMember.id)
        addStaffIdRouteParam(undefined)
      }
    } catch ({ message }) {
      setErrorMessage(message)
    } finally {
      setDeletePending(false)
    }
  }

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ width: '100%' }}>
        <MaintenanceStaffList onSelectStaffMember={onSelectStaffMember} isLoading={isLoading} />
        {buildingId && (
          <div className="maintenance-staff__add-new-cnt">
            <button
              type="submit"
              onClick={onAddNewClick}
              className={`button button-primary is-Block  maintenance-staff__add-new-btn`}
            >
              {t('maintenance:staff.addNew')}
            </button>
          </div>
        )}
      </div>
      {isFormOpen && (
        <MaintenanceStaffForm
          errorMessage={errorMessage}
          onSubmit={createOrUpdateStuff}
          onDeleteStaff={onDeleteStaff}
          selectedMember={currentStuffMember}
          isDeletePending={isDeletePending}
        />
      )}
    </div>
  )
}

MaintenanceStaff.propTypes = {
  fetchStaff: PropTypes.func,
  deleteStaffPerson: PropTypes.func,
  updateStaffPerson: PropTypes.func,
  createStaffPerson: PropTypes.func,
  staffPersons: PropTypes.array,
  userData: PropTypes.object,
}

export default connect(
  (state) => ({
    staffPersons: selectStaffPersons(state),
    userData: selectUserData(state),
  }),
  {
    fetchStaff,
    createStaffPerson,
    updateStaffPerson,
    deleteStaffPerson,
  },
)(MaintenanceStaff, createStaffPerson)
